import { FC } from "react";
import { useAppDispatch, useAppSelector } from "@src/reduxStore";
import { changeLanguage } from "@src/common/commonSlice";
import { useOrgInfo, useTopMenu } from "@src/common/reliablePartnerApi";
import { useHistory } from "react-router-dom";
import { Language, setCookieLang } from "@src/i18n";
import LazyStvUtilsFrontend from "@src/LazyStvUtilsFrontend";

const TopMenu: FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const language = useAppSelector(state => state.common.language);
  const hasOrgContext = useAppSelector(state => state.common.hasOrgContext);
  const topMenuQuery = useTopMenu();
  const orgInfo = useOrgInfo();

  const selectLanguage = (lang: Language) => {
    dispatch(changeLanguage(lang));
    void setCookieLang(lang);
  };

  const loaderIndicator = (
    <LoaderIndicator language={language} selectLanguage={selectLanguage} />
  );

  switch (topMenuQuery.status) {
    case "idle":
    case "loading":
      return loaderIndicator;
    case "success":
      const topMenuParameters = topMenuQuery.data;
      const noSelectedOrg =
        !hasOrgContext || (orgInfo.isSuccess && !orgInfo.data);
      return (
        <LazyStvUtilsFrontend fallback={loaderIndicator}>
          {({ TopMenuComponent }) => (
            <TopMenuComponent
              topMenuParameters={{
                ...topMenuParameters,
                selectedRepresentationIndex: noSelectedOrg
                  ? null
                  : topMenuParameters.selectedRepresentationIndex,
              }}
              selectedLanguageCode={language}
              onLanguageChanged={selectLanguage}
              onSelectedRepresentationChanged={(newOrg: number) => {
                const {
                  data: {
                    allRepresentationCodes: { [newOrg]: newOrgId },
                  },
                } = topMenuQuery;
                history.push(`/orgs/${newOrgId}`);
              }}
              onSignOutClicked={() => (window.location.href = "/api/logout")}
            />
          )}
        </LazyStvUtilsFrontend>
      );
    case "error":
      throw topMenuQuery.error;
  }
};

export default TopMenu;

interface LoaderProps {
  language: Language;
  selectLanguage: (lang: Language) => void;
}

/**
 * Static placeholder until we have all the required data.
 * Provides language selector.
 */
const LoaderIndicator: FC<LoaderProps> = ({ language, selectLanguage }) => {
  const config = useAppSelector(state => state.common.config);
  const langMap: Record<Language, string> = {
    en: "English",
    et: "Eesti",
    fi: "Suomi",
    sv: "Svenska",
  };
  return (
    <div className="top-menu-wrapper fixed-top">
      <nav className="navbar stv-primary-nav navbar-toggleable-sm">
        <a
          role="button"
          className="navbar-brand"
          id="top_menu_logo"
          href={config?.portalUrl}
        >
          <span></span>
        </a>
        <button
          id="top_menu_collapse_button"
          className="navbar-toggler navbar-toggler-right"
          type="button"
          data-toggle="collapse"
          data-target="#collapsingNavbar"
          aria-controls="collapsingNavbar"
          aria-expanded="false"
          aria-label="Toggle navigation"
        >
          ☰
        </button>
        <div className="collapse navbar-collapse" id="collapsingNavbar">
          <ul id="top_menu_signed_out_right_group" className="navbar-nav">
            <li className="nav-item dropdown">
              <button
                id="top_menu_lang_dropdown"
                className="nav-link dropdown-toggle"
                data-toggle="dropdown"
                type="button"
                aria-haspopup="true"
                aria-expanded="false"
              >
                <span id="top_menu_lang_dropdown_text">
                  {langMap[language]}
                </span>
              </button>
              <div
                className="dropdown-menu dropdown-menu-right"
                aria-labelledby="Preview"
              >
                <button
                  type="button"
                  id="top_menu_lang_fi"
                  className="dropdown-item with-icon"
                  title="Suomi"
                  onClick={() => selectLanguage("fi")}
                >
                  <i
                    id="top_menu_lang_fi_checked"
                    className="fa fa-check fa-grey"
                    aria-hidden="true"
                  ></i>
                  <span id="top_menu_lang_fi_text">Suomi</span>
                </button>
                <button
                  type="button"
                  id="top_menu_lang_sv"
                  className="dropdown-item with-icon"
                  title="Svenska"
                  onClick={() => selectLanguage("sv")}
                >
                  <i
                    id="top_menu_lang_sv_checked"
                    className="fa fa-check fa-grey selected-language-icon-hidden"
                    aria-hidden="true"
                  ></i>
                  <span id="top_menu_lang_sv_text">Svenska</span>
                </button>
                <button
                  type="button"
                  id="top_menu_lang_et"
                  className="dropdown-item with-icon"
                  title="Eesti"
                  onClick={() => selectLanguage("et")}
                >
                  <i
                    id="top_menu_lang_et_checked"
                    className="fa fa-check fa-grey selected-language-icon-hidden"
                    aria-hidden="true"
                  ></i>
                  <span id="top_menu_lang_et_text">Eesti</span>
                </button>
                <button
                  type="button"
                  id="top_menu_lang_en"
                  className="dropdown-item with-icon"
                  title="English"
                  onClick={() => selectLanguage("en")}
                >
                  <i
                    id="top_menu_lang_en_checked"
                    className="fa fa-check fa-grey selected-language-icon-hidden"
                    aria-hidden="true"
                  ></i>
                  <span id="top_menu_lang_en_text">English</span>
                </button>
              </div>
            </li>
          </ul>
        </div>
      </nav>
    </div>
  );
};
